import React, { useState, useEffect } from 'react';
import { BarChart2, Plus, Trash2, CheckCircle, HelpCircle, Lightbulb, ChevronUp, Clock, Folder, Lock, Unlock, MessageSquare, Send } from 'lucide-react';
import { UserAvatar } from './App';

export default function PollsTab({ ctx }) {
  const { T, iS, api, notify, isAdmin, isEN, canManagePolls, setTaskForm, taskForm, setModal, users } = ctx;
  
  const [viewMode, setViewMode] = useState("polls");
  const [polls, setPolls] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [ideaThemes, setIdeaThemes] = useState([]);
  const [selectedThemeId, setSelectedThemeId] = useState("");
  const [loading, setLoading] = useState(true);
  
  const [showPollModal, setShowPollModal] = useState(false);
  const [newPoll, setNewPoll] = useState({ question: "", options: ["", ""], expires_at: "" });

  const [showIdeaModal, setShowIdeaModal] = useState(false);
  const [newIdea, setNewIdea] = useState({ title: "", description: "" });

  const [showThemeModal, setShowThemeModal] = useState(false);
  const [newTheme, setNewTheme] = useState({ title: "", description: "" });

  const [expandedIdea, setExpandedIdea] = useState(null);
  const [ideaComments, setIdeaComments] = useState({});
  const [commentText, setCommentText] = useState("");

  // 🚀 NEU: Speichert lokal im Browser, wann du welche Idee zuletzt gelesen hast
  const [readStates, setReadStates] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tf_idea_reads")) || {}; }
    catch { return {}; }
  });

  const IDEA_STATUSES = [
    { value: 'Neu', label: isEN ? 'New' : 'Neu', color: '#94A3B8' },
    { value: 'Wird geprüft', label: isEN ? 'Under Review' : 'Wird geprüft', color: '#F59E0B' },
    { value: 'Geplant', label: isEN ? 'Planned' : 'Geplant', color: '#8B5CF6' },
    { value: 'Umgesetzt', label: isEN ? 'Implemented' : 'Umgesetzt', color: '#34D399' },
    { value: 'Abgelehnt', label: isEN ? 'Rejected' : 'Abgelehnt', color: '#FF4757' }
  ];

  const loadData = async () => {
    setLoading(true);
    try {
      const pData = await api('/polls');
      setPolls(pData);
      
      const tData = await api('/idea-themes');
      setIdeaThemes(tData);
      
      let currentThemeId = selectedThemeId;
      if (!currentThemeId && tData.length > 0) {
        const active = tData.find(t => t.is_active) || tData[0];
        currentThemeId = active.id;
        setSelectedThemeId(currentThemeId);
      }
      
      if (currentThemeId) {
        const iData = await api(`/ideas?theme_id=${currentThemeId}`);
        setIdeas(iData);
      } else {
        setIdeas([]);
      }
    } catch(e) { notify((isEN ? "Load error: " : "Fehler beim Laden: ") + e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [viewMode]);

  useEffect(() => {
    if (viewMode === "ideas" && selectedThemeId) {
      api(`/ideas?theme_id=${selectedThemeId}`).then(setIdeas).catch(()=>{});
    }
  }, [selectedThemeId, viewMode, api]);

  // --- POLLS LOGIC ---
  const handleVotePoll = async (pollId, optionId, isExpired) => {
    if (isExpired) return notify(isEN ? "Poll is closed." : "Diese Umfrage ist bereits beendet.");
    try {
      await api(`/polls/${pollId}/vote`, { method: 'POST', body: JSON.stringify({ option_id: optionId }) });
      loadData();
    } catch(e) { notify((isEN ? "Voting error: " : "Fehler beim Abstimmen: ") + e.message); }
  };

  const handleCreatePoll = async () => {
    if (!newPoll.question.trim() || newPoll.options.filter(o => o.trim()).length < 2) return notify(isEN ? "Question and min. 2 options required!" : "Frage und min. 2 Optionen erforderlich!");
    try {
      let expiry = null;
      if (newPoll.expires_at) expiry = new Date(newPoll.expires_at).toISOString();
      await api('/polls', { method: 'POST', body: JSON.stringify({ ...newPoll, expires_at: expiry }) });
      notify(isEN ? "Poll created ✅" : "Umfrage erstellt ✅");
      setShowPollModal(false);
      setNewPoll({ question: "", options: ["", ""], expires_at: "" });
      loadData();
    } catch(e) { notify("Error: " + e.message); }
  };

  const handleDeletePoll = async (id) => {
    if (!window.confirm(isEN ? "Delete poll?" : "Umfrage löschen?")) return;
    try { await api(`/polls/${id}`, { method: 'DELETE' }); notify(isEN ? "Poll deleted 🗑" : "Umfrage gelöscht 🗑"); loadData(); } catch(e) { notify("Error: " + e.message); }
  };

  // --- IDEAS LOGIC ---
  const handleVoteIdea = async (ideaId) => {
    try {
      await api(`/ideas/${ideaId}/vote`, { method: 'POST' });
      const iData = await api(`/ideas?theme_id=${selectedThemeId}`);
      setIdeas(iData);
    } catch(e) { notify("Error: " + e.message); }
  };

  const handleCreateIdea = async () => {
    if (!newIdea.title.trim()) return notify(isEN ? "Title required!" : "Titel erforderlich!");
    try {
      await api('/ideas', { method: 'POST', body: JSON.stringify({ ...newIdea, theme_id: selectedThemeId }) });
      notify(isEN ? "Idea submitted 💡" : "Idee eingereicht 💡");
      setShowIdeaModal(false);
      setNewIdea({ title: "", description: "" });
      const iData = await api(`/ideas?theme_id=${selectedThemeId}`);
      setIdeas(iData);
    } catch(e) { notify("Error: " + e.message); }
  };

  const handleDeleteIdea = async (id) => {
    if (!window.confirm(isEN ? "Delete idea?" : "Idee löschen?")) return;
    try { 
      await api(`/ideas/${id}`, { method: 'DELETE' }); notify(isEN ? "Idea deleted 🗑" : "Idee gelöscht 🗑"); 
      const iData = await api(`/ideas?theme_id=${selectedThemeId}`);
      setIdeas(iData);
    } catch(e) { notify("Error: " + e.message); }
  };

  const handleStatusChange = async (ideaId, newStatus) => {
    try {
      await api(`/ideas/${ideaId}/status`, { method: 'PUT', body: JSON.stringify({ status: newStatus }) });
      notify(isEN ? "Status updated ✅" : "Status aktualisiert ✅");
      const iData = await api(`/ideas?theme_id=${selectedThemeId}`);
      setIdeas(iData);
    } catch(e) { notify("Error: " + e.message); }
  };

  // --- IDEAS COMMENTS LOGIC ---
  const loadComments = async (ideaId) => {
    try {
      const res = await api(`/ideas/${ideaId}/comments`);
      setIdeaComments(prev => ({ ...prev, [ideaId]: res }));
    } catch(e) { console.error(e); }
  };

  const toggleComments = (ideaId) => {
    if (expandedIdea === ideaId) {
      setExpandedIdea(null);
    } else {
      setExpandedIdea(ideaId);
      loadComments(ideaId);
      
      // 🚀 Markiert die Idee als "Gelesen" beim Aufklappen
      const newReads = { ...readStates, [ideaId]: Date.now() };
      setReadStates(newReads);
      localStorage.setItem("tf_idea_reads", JSON.stringify(newReads));
    }
  };

  const handleAddComment = async (ideaId) => {
    if (!commentText.trim()) return;
    try {
      await api(`/ideas/${ideaId}/comments`, { method: 'POST', body: JSON.stringify({ text: commentText }) });
      setCommentText("");
      loadComments(ideaId);
      
      // 🚀 Zähler live in der Liste hochsetzen, damit es sich sofort ändert
      setIdeas(prev => prev.map(i => i.id === ideaId ? { 
        ...i, 
        comment_count: parseInt(i.comment_count || 0) + 1,
        last_comment_at: new Date().toISOString()
      } : i));

      // Eigene Kommentare sind logischerweise direkt "gelesen"
      const newReads = { ...readStates, [ideaId]: Date.now() };
      setReadStates(newReads);
      localStorage.setItem("tf_idea_reads", JSON.stringify(newReads));

    } catch(e) { notify("Error: " + e.message); }
  };

  // --- THEMES LOGIC ---
  const handleCreateTheme = async () => {
    if (!newTheme.title.trim()) return notify(isEN ? "Theme needs a title!" : "Thema benötigt einen Titel!");
    try {
      const res = await api('/idea-themes', { method: 'POST', body: JSON.stringify(newTheme) });
      notify(isEN ? "New theme set 🎯" : "Neues Thema gesetzt 🎯");
      setShowThemeModal(false);
      setNewTheme({ title: "", description: "" });
      setSelectedThemeId(res.id);
      loadData();
    } catch(e) { notify("Error: " + e.message); }
  };

  const handleToggleThemeLock = async (id) => {
    try {
      await api(`/idea-themes/${id}/toggle-lock`, { method: 'PATCH' });
      loadData();
    } catch(e) { notify("Error: " + e.message); }
  };

  const handleDeleteTheme = async (id) => {
    if (!window.confirm(isEN ? "Delete this theme AND all its ideas completely?" : "Dieses Thema UND alle dazugehörigen Ideen komplett löschen?")) return;
    try {
      await api(`/idea-themes/${id}`, { method: 'DELETE' });
      setSelectedThemeId("");
      loadData();
    } catch(e) { notify("Error: " + e.message); }
  };

  if (loading) return <div style={{ color: T.textFaint, textAlign: "center", padding: 40 }}>{isEN ? "Loading..." : "Laden..."}</div>;

  const currentTheme = ideaThemes.find(t => t.id === Number(selectedThemeId));
  const isThemeLocked = currentTheme?.is_locked;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", background: T.card, padding: "20px 24px", borderRadius: 16, border: `1px solid ${T.border}`, boxShadow: "0 4px 12px rgba(0,0,0,0.02)", gap: 16 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: T.text, display: "flex", alignItems: "center", gap: 10 }}>
            {viewMode === "polls" ? <BarChart2 color="#0ECFD9" size={26} /> : <Lightbulb color="#F59E0B" size={26} />}
            {viewMode === "polls" ? (isEN ? "Team Polls" : "Team-Umfragen") : (isEN ? "Idea Board" : "Ideen-Modus")}
          </h2>
          <p style={{ color: T.textMuted, fontSize: 13, marginTop: 4 }}>
            {viewMode === "polls" ? (isEN ? "Vote on structured team decisions." : "Stimme über vorgegebene Team-Entscheidungen ab.") : (isEN ? "Propose new features and upvote the best ideas." : "Schlage neue Features vor und bewerte die Ideen anderer.")}
          </p>
        </div>

        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", background: T.input, border: `1px solid ${T.border2}`, borderRadius: 11, padding: 4 }}>
            <button onClick={() => setViewMode("polls")} style={{ background: viewMode === "polls" ? T.card : "transparent", color: viewMode === "polls" ? T.text : T.textMuted, border: "none", padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: viewMode === "polls" ? "0 2px 8px rgba(0,0,0,0.05)" : "none" }}>
              <BarChart2 size={16}/> {isEN ? "Polls" : "Umfragen"}
            </button>
            <button onClick={() => setViewMode("ideas")} style={{ background: viewMode === "ideas" ? T.card : "transparent", color: viewMode === "ideas" ? T.text : T.textMuted, border: "none", padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: viewMode === "ideas" ? "0 2px 8px rgba(0,0,0,0.05)" : "none" }}>
              <Lightbulb size={16}/> {isEN ? "Ideas" : "Ideen"}
            </button>
          </div>

          {viewMode === "polls" && canManagePolls && (
            <button onClick={() => setShowPollModal(true)} style={{ background: "#0ECFD9", color: "#fff", border: "none", borderRadius: 10, padding: "10px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              <Plus size={16} /> {isEN ? "New Poll" : "Neue Umfrage"}
            </button>
          )}
          {viewMode === "ideas" && (
            <button onClick={() => setShowIdeaModal(true)} disabled={!selectedThemeId || isThemeLocked} style={{ background: isThemeLocked ? T.border3 : "#F59E0B", color: isThemeLocked ? T.textMuted : "#fff", border: "none", borderRadius: 10, padding: "10px 18px", fontSize: 13, fontWeight: 700, cursor: (!selectedThemeId || isThemeLocked) ? "not-allowed" : "pointer", opacity: (!selectedThemeId || isThemeLocked) ? 0.6 : 1, display: "flex", alignItems: "center", gap: 6 }}>
              {isThemeLocked ? <Lock size={16} /> : <Plus size={16} />} {isThemeLocked ? (isEN ? "Theme Locked" : "Thema gesperrt") : (isEN ? "Submit Idea" : "Idee einreichen")}
            </button>
          )}
        </div>
      </div>

      {viewMode === "polls" && (
        polls.length === 0 ? (
          <div style={{ padding: 60, textAlign: "center", color: T.textFaint, background: T.card, border: `1px solid ${T.border}`, borderRadius: 16 }}><HelpCircle size={48} style={{ opacity: 0.5, margin: "0 auto 16px" }} /><div>{isEN ? "No active polls right now." : "Aktuell gibt es keine aktiven Umfragen."}</div></div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 16 }}>
            {polls.map(poll => (
              <div key={poll.id} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "24px", display: "flex", flexDirection: "column", boxShadow: "0 4px 12px rgba(0,0,0,0.02)", opacity: poll.isExpired ? 0.7 : 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: T.text, lineHeight: 1.4, marginBottom: 6 }}>{poll.question}</div>
                    {poll.expires_at && (
                      <div style={{ fontSize: 11, color: poll.isExpired ? "#FF4757" : T.textMuted, display: "flex", alignItems: "center", gap: 4, fontWeight: 600 }}>
                        <Clock size={12} /> {poll.isExpired ? (isEN ? "Ended on " : "Beendet am ") : (isEN ? "Ends on " : "Endet am ")} 
                        {new Date(poll.expires_at).toLocaleString(isEN ? 'en-US' : 'de-DE', { dateStyle: 'short', timeStyle: 'short' })} {isEN?"":"Uhr"}
                      </div>
                    )}
                  </div>
                  {canManagePolls && <button onClick={() => handleDeletePoll(poll.id)} style={{ background: "transparent", border: "none", color: "#FF4757", cursor: "pointer", padding: 4 }} className="hov" title={isEN ? "Delete" : "Löschen"}><Trash2 size={16} /></button>}
                </div>
                
                <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
                  {poll.options.map(opt => {
                    const pct = poll.totalVotes > 0 ? Math.round((opt.votes / poll.totalVotes) * 100) : 0;
                    const isWinner = poll.totalVotes > 0 && opt.votes === Math.max(...poll.options.map(o => o.votes));
                    return (
                      <div key={opt.id} onClick={() => handleVotePoll(poll.id, opt.id, poll.isExpired)} style={{ position: "relative", padding: "12px 16px", borderRadius: 12, border: `1px solid ${opt.votedByMe ? "#0ECFD9" : T.border2}`, background: opt.votedByMe ? "rgba(14,207,217,0.05)" : T.input, cursor: poll.isExpired ? "default" : "pointer", overflow: "hidden", transition: "all 0.2s" }} className={!poll.isExpired ? "hov" : ""}>
                        {poll.totalVotes > 0 && <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct}%`, background: opt.votedByMe ? "rgba(14,207,217,0.15)" : T.cardHover, zIndex: 0, transition: "width 0.5s ease" }} />}
                        <div style={{ position: "relative", zIndex: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: 14, fontWeight: opt.votedByMe ? 700 : 500, color: opt.votedByMe ? "#0ECFD9" : T.text, display: "flex", alignItems: "center", gap: 8 }}>{opt.votedByMe && <CheckCircle size={14} />} {opt.text}</span>
                          {poll.totalVotes > 0 && <span style={{ fontSize: 13, fontWeight: 700, color: isWinner ? T.text : T.textMuted }}>{pct}% <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.6 }}>({opt.votes})</span></span>}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: 11, color: T.textFaint }}>{isEN ? "Created by" : "Erstellt von"} <span style={{ fontWeight: 600, color: T.textMuted }}>{poll.creator_name || "System"}</span></div>
                  <div style={{ fontSize: 11, background: T.cardHover, padding: "4px 10px", borderRadius: 8, color: T.textMuted, fontWeight: 600 }}>{poll.totalVotes} {isEN ? "Votes" : "Stimmen"}</div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {viewMode === "ideas" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.textMuted }}>{isEN ? "SELECTED THEME:" : "GEWÄHLTES THEMA:"}</div>
              <select value={selectedThemeId} onChange={e => setSelectedThemeId(e.target.value)} style={{ ...iS, width: "auto", minWidth: 200, fontWeight: 600 }}>
                {ideaThemes.length === 0 && <option value="">{isEN ? "No themes available" : "Kein Thema vorhanden"}</option>}
                {ideaThemes.map(t => <option key={t.id} value={t.id}>{t.title} {t.is_locked ? (isEN ? " (Locked)" : " (Gesperrt)") : (t.is_active ? (isEN ? " (Active)" : " (Aktiv)") : (isEN ? " (Archive)" : " (Archiv)"))}</option>)}
              </select>
              
              {canManagePolls && ideaThemes.length > 0 && selectedThemeId && (
                <div style={{ display: "flex", gap: 4 }}>
                  <button onClick={() => handleToggleThemeLock(selectedThemeId)} style={{ background: "transparent", border: "none", color: isThemeLocked ? "#FF4757" : T.textMuted, cursor: "pointer", padding: 8 }} title={isThemeLocked ? (isEN ? "Unlock Theme" : "Thema entsperren") : (isEN ? "Lock Theme" : "Thema sperren")} className="hov">
                    {isThemeLocked ? <Lock size={16} /> : <Unlock size={16} />}
                  </button>
                  <button onClick={() => handleDeleteTheme(selectedThemeId)} style={{ background: "transparent", border: "none", color: "#FF4757", cursor: "pointer", padding: 8 }} title={isEN ? "Delete Theme" : "Thema löschen"} className="hov">
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>

            {canManagePolls && (
              <button onClick={() => setShowThemeModal(true)} style={{ background: T.cardHover, border: `1px solid ${T.border2}`, color: T.text, padding: "8px 16px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }} className="hov">
                <Folder size={14} /> + {isEN ? "Create New Theme" : "Neues Thema vorgeben"}
              </button>
            )}
          </div>

          {currentTheme && currentTheme.description && (
            <div style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 12, padding: "16px" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#F59E0B", marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                {currentTheme.title} {isThemeLocked && <Lock size={14} />}
              </div>
              <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.5 }}>{currentTheme.description}</div>
            </div>
          )}

          {ideas.length === 0 ? (
            <div style={{ padding: 60, textAlign: "center", color: T.textFaint, background: T.card, border: `1px solid ${T.border}`, borderRadius: 16 }}><Lightbulb size={48} style={{ opacity: 0.5, margin: "0 auto 16px" }} /><div>{isEN ? "No ideas submitted for this theme yet." : "Noch keine Ideen zu diesem Thema eingereicht."}</div></div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {ideas.map((idea, index) => {
                const sObj = IDEA_STATUSES.find(s => s.value === idea.status) || IDEA_STATUSES[0];
                
                // 🚀 NEU: Holt die Variablen für den Zähler und den roten Ungelesen-Punkt
                const cCount = parseInt(idea.comment_count) || 0;
                const lastCommentTime = idea.last_comment_at ? new Date(idea.last_comment_at).getTime() : 0;
                const lastReadTime = readStates[idea.id] || 0;
                const hasUnread = cCount > 0 && lastCommentTime > lastReadTime;

                return (
                  <div key={idea.id} style={{ display: "flex", gap: 16, background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "20px", boxShadow: "0 4px 12px rgba(0,0,0,0.02)" }}>
                    {/* UPVOTE COLUMN */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                      <button onClick={() => handleVoteIdea(idea.id)} style={{ width: 44, height: 44, borderRadius: 12, border: `1px solid ${idea.votedByMe ? "#F59E0B" : T.border2}`, background: idea.votedByMe ? "rgba(245,158,11,0.1)" : T.input, color: idea.votedByMe ? "#F59E0B" : T.textMuted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }} className="hov"><ChevronUp size={24} strokeWidth={idea.votedByMe ? 3 : 2} /></button>
                      <span style={{ fontSize: 15, fontWeight: 800, color: idea.votedByMe ? "#F59E0B" : T.text }}>{idea.votes}</span>
                    </div>

                    <div style={{ flex: 1 }}>
                      {/* HEADER: TITLE, BADGE & MAGIC BUTTON */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, flexWrap: "wrap", gap: 10 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: T.text, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                          {index === 0 && idea.votes > 0 && <span style={{ fontSize: 12, background: "rgba(245,158,11,0.15)", color: "#F59E0B", padding: "3px 8px", borderRadius: 8 }}>🔥 Trending</span>}
                          {idea.title}
                        </div>
                        
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {/* 🚀 THE MAGIC BUTTON */}
                          {canManagePolls && (idea.status === 'Geplant' || idea.status === 'Umgesetzt') && (
                            <button onClick={() => {
                              setTaskForm({ ...taskForm, title: idea.title, desc: `**Ursprüngliche Idee:**\n${idea.description}`, status: "Offen", priority: 2 });
                              setModal("task");
                            }} style={{ background: "rgba(139, 92, 246, 0.15)", color: "#8B5CF6", border: "1px solid rgba(139, 92, 246, 0.3)", padding: "4px 10px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", gap: 6, alignItems: "center" }} className="hov">
                              🪄 In Aufgabe umwandeln
                            </button>
                          )}

                          {/* 🚀 STATUS BADGE / DROPDOWN */}
                          {canManagePolls ? (
                            <select 
                              value={idea.status || 'Neu'} 
                              onChange={e => handleStatusChange(idea.id, e.target.value)}
                              style={{ background: sObj.color + "20", color: sObj.color, border: `1px solid ${sObj.color}40`, padding: "4px 8px", borderRadius: 8, fontSize: 12, fontWeight: 700, outline: "none", cursor: "pointer", appearance: "none" }}
                            >
                              {IDEA_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                          ) : (
                            <span style={{ background: sObj.color + "20", color: sObj.color, padding: "4px 8px", borderRadius: 8, fontSize: 12, fontWeight: 700, border: `1px solid ${sObj.color}40` }}>
                              {sObj.label}
                            </span>
                          )}

                          {canManagePolls && <button onClick={() => handleDeleteIdea(idea.id)} style={{ background: "transparent", border: "none", color: "#FF4757", cursor: "pointer", padding: 4 }} className="hov"><Trash2 size={16} /></button>}
                        </div>
                      </div>

                      <div style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.6, whiteSpace: "pre-wrap", marginBottom: 12 }}>{idea.description}</div>
                      
                      <div style={{ fontSize: 11, color: T.textFaint, display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 16, height: 16, borderRadius: 4, background: idea.creator_color || "#94A3B8", display: "inline-block" }} /> {isEN ? "Submitted by" : "Eingereicht von"} <b style={{ color: T.textSub }}>{idea.creator_name || "System"}</b>
                      </div>

                      {/* 🚀 KOMMENTAR BEREICH */}
                      <div style={{ marginTop: 16, borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
                        
                        {/* 🚀 NEUER BUTTON MIT ZÄHLER UND ALARM-PUNKT */}
                        <button onClick={() => toggleComments(idea.id)} style={{ background: "transparent", border: "none", color: T.textMuted, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, position: "relative" }} className="hov">
                          <MessageSquare size={16} /> 
                          {expandedIdea === idea.id ? (isEN ? "Hide Comments" : "Kommentare ausblenden") : (isEN ? `Comments (${cCount})` : `Kommentare (${cCount})`)}
                          
                          {/* 🔴 Der Unread-Ping */}
                          {hasUnread && expandedIdea !== idea.id && (
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#FF4757", boxShadow: "0 0 8px #FF4757", marginLeft: 4 }} title="Neue Kommentare!" />
                          )}
                        </button>
                        
                        {expandedIdea === idea.id && (
                          <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 12, animation: "fadeUp 0.2s ease" }}>
                            {(ideaComments[idea.id] || []).length === 0 ? (
                              <div style={{ fontSize: 12, color: T.textFaint, fontStyle: "italic", background: T.cardHover, padding: 12, borderRadius: 10 }}>{isEN ? "No comments yet. Be the first!" : "Noch keine Kommentare. Mach den Anfang!"}</div>
                            ) : (
                              (ideaComments[idea.id] || []).map(c => (
                                <div key={c.id} style={{ display: "flex", gap: 10 }}>
                                  <UserAvatar user={users.find(u => u.id === c.author_id)} size={28} />
                                  <div style={{ flex: 1, background: T.cardHover, padding: "10px 14px", borderRadius: "0 12px 12px 12px", border: `1px solid ${T.border2}` }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: T.textSub, marginBottom: 4, display: "flex", justifyContent: "space-between" }}>
                                      {c.author_name || "System"} 
                                      <span style={{ fontSize: 10, color: T.textFaint, fontWeight: 400 }}>{new Date(c.created_at).toLocaleDateString()}</span>
                                    </div>
                                    <div style={{ fontSize: 13, color: T.text, lineHeight: 1.4 }}>{c.text}</div>
                                  </div>
                                </div>
                              ))
                            )}
                            
                            {/* EINGABEFELD FÜR NEUE KOMMENTARE */}
                            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                              <input value={commentText} onChange={e => setCommentText(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAddComment(idea.id)} placeholder={isEN ? "Write a comment..." : "Kommentar schreiben..."} style={{ ...iS, flex: 1 }} />
                              <button onClick={() => handleAddComment(idea.id)} style={{ background: "#0ECFD9", color: "#0A0D15", border: "none", width: 38, height: 38, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", opacity: commentText.trim() ? 1 : 0.5 }} disabled={!commentText.trim()}><Send size={16}/></button>
                            </div>
                          </div>
                        )}
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* MODALS */}
      {showPollModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: T.sidebar, border: `1px solid ${T.border3}`, borderRadius: 22, padding: "32px", width: 500, boxShadow: "0 32px 72px rgba(0,0,0,0.6)" }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 20, display: "flex", alignItems: "center", gap: 8 }}><BarChart2 color="#0ECFD9" /> {isEN ? "New Poll" : "Neue Umfrage"}</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div><div style={{ fontSize: 10, color: T.textFaint, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 8 }}>{isEN ? "QUESTION" : "FRAGE"}</div><input value={newPoll.question} onChange={e => setNewPoll({...newPoll, question: e.target.value})} placeholder={isEN ? "What is it about?" : "Worum geht es?"} style={iS} autoFocus /></div>
              <div><div style={{ fontSize: 10, color: T.textFaint, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 8 }}>{isEN ? "ENDS ON (OPTIONAL)" : "ENDET AM (OPTIONAL)"}</div><input type="datetime-local" value={newPoll.expires_at} onChange={e => setNewPoll({...newPoll, expires_at: e.target.value})} style={iS} /></div>
              <div>
                <div style={{ fontSize: 10, color: T.textFaint, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 8 }}>{isEN ? "OPTIONS" : "ANTWORTEN"}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {newPoll.options.map((opt, idx) => (
                    <div key={idx} style={{ display: "flex", gap: 8 }}>
                      <input value={opt} onChange={e => { const n = [...newPoll.options]; n[idx] = e.target.value; setNewPoll({...newPoll, options: n}); }} placeholder={`Option ${idx + 1}`} style={{ ...iS, flex: 1 }} />
                      {newPoll.options.length > 2 && <button onClick={() => setNewPoll({...newPoll, options: newPoll.options.filter((_, i) => i !== idx)})} style={{ background: "transparent", border: "none", color: "#FF4757", cursor: "pointer" }}><Trash2 size={16} /></button>}
                    </div>
                  ))}
                  <button onClick={() => setNewPoll({...newPoll, options: [...newPoll.options, ""]})} style={{ background: "transparent", border: `1px dashed ${T.border3}`, color: T.textMuted, padding: "8px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{isEN ? "+ Add Option" : "+ Weitere Option"}</button>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 28 }}><button onClick={() => setShowPollModal(false)} style={{ background: "transparent", border: `1px solid ${T.border2}`, color: T.textMuted, padding: "9px 18px", borderRadius: 9, cursor: "pointer" }}>{isEN ? "Cancel" : "Abbrechen"}</button><button onClick={handleCreatePoll} style={{ background: "#0ECFD9", border: "none", color: "#0A0A0F", padding: "9px 22px", borderRadius: 9, fontWeight: 700, cursor: "pointer" }}>{isEN ? "Start" : "Starten"}</button></div>
          </div>
        </div>
      )}

      {showIdeaModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: T.sidebar, border: `1px solid ${T.border3}`, borderRadius: 22, padding: "32px", width: 500, boxShadow: "0 32px 72px rgba(0,0,0,0.6)" }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 20, display: "flex", alignItems: "center", gap: 8 }}><Lightbulb color="#F59E0B" /> {isEN ? "Submit Idea" : "Idee einreichen"}</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div><div style={{ fontSize: 10, color: T.textFaint, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 8 }}>{isEN ? "TITLE" : "TITEL"}</div><input value={newIdea.title} onChange={e => setNewIdea({...newIdea, title: e.target.value})} placeholder={isEN ? "Short, catchy name for the idea" : "Kurzer, prägnanter Name der Idee"} style={iS} autoFocus /></div>
              <div><div style={{ fontSize: 10, color: T.textFaint, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 8 }}>{isEN ? "DESCRIPTION" : "BESCHREIBUNG"}</div><textarea value={newIdea.description} onChange={e => setNewIdea({...newIdea, description: e.target.value})} placeholder={isEN ? "What exactly do you want and why?" : "Was genau wünschst du dir und warum?"} style={{...iS, height: 120, resize: "none"}} /></div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 28 }}><button onClick={() => setShowIdeaModal(false)} style={{ background: "transparent", border: `1px solid ${T.border2}`, color: T.textMuted, padding: "9px 18px", borderRadius: 9, cursor: "pointer" }}>{isEN ? "Cancel" : "Abbrechen"}</button><button onClick={handleCreateIdea} style={{ background: "#F59E0B", border: "none", color: "#fff", padding: "9px 22px", borderRadius: 9, fontWeight: 700, cursor: "pointer" }}>{isEN ? "Submit" : "Einreichen"}</button></div>
          </div>
        </div>
      )}

      {showThemeModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: T.sidebar, border: `1px solid ${T.border3}`, borderRadius: 22, padding: "32px", width: 500, boxShadow: "0 32px 72px rgba(0,0,0,0.6)" }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 20, display: "flex", alignItems: "center", gap: 8 }}><Folder color="#8B5CF6" /> {isEN ? "Create New Theme" : "Neues Thema vorgeben"}</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div><div style={{ fontSize: 10, color: T.textFaint, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 8 }}>{isEN ? "THEME TITLE" : "THEMEN-TITEL"}</div><input value={newTheme.title} onChange={e => setNewTheme({...newTheme, title: e.target.value})} placeholder={isEN ? "e.g. Q3 Design Ideas" : "z.B. Q3 Design Ideen"} style={iS} autoFocus /></div>
              <div><div style={{ fontSize: 10, color: T.textFaint, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 8 }}>{isEN ? "DESCRIPTION (OPTIONAL)" : "BESCHREIBUNG (OPTIONAL)"}</div><textarea value={newTheme.description} onChange={e => setNewTheme({...newTheme, description: e.target.value})} placeholder={isEN ? "Briefly explain what this theme is about..." : "Erkläre kurz, worum es bei diesem Thema geht..."} style={{...iS, height: 100, resize: "none"}} /></div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 28 }}><button onClick={() => setShowThemeModal(false)} style={{ background: "transparent", border: `1px solid ${T.border2}`, color: T.textMuted, padding: "9px 18px", borderRadius: 9, cursor: "pointer" }}>{isEN ? "Cancel" : "Abbrechen"}</button><button onClick={handleCreateTheme} style={{ background: "#8B5CF6", border: "none", color: "#fff", padding: "9px 22px", borderRadius: 9, fontWeight: 700, cursor: "pointer" }}>{isEN ? "Start Theme" : "Thema starten"}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}